import * as Tone from 'tone';
import { colors, Notify } from "quasar";
import { getAssetItem } from 'src/utils/helpers';

import { KEYS_TO_NOTES } from 'src/modules/musical-components/keyboard/models/Keyboard';
import { PIANO_SAMPLES } from "./data/samples-piano";
import { FLUTE_SAMPLES } from "./data/samples-flute";

import { MidiKeyEvent } from "../midi/models/MidiKeyEvent";
import { SynthKeys } from "./models/SynthKeys";

import { TinyEmitter } from 'tiny-emitter';
import audioPlayer from './AudioPlayer';
import Vue from 'vue';

// Raw compressed audio bytes keyed by URL — survives AudioContext replacements.
// decodeAudioData requires a copy (slice) because some browsers detach the buffer.
const rawSampleCache = new Map<string, ArrayBuffer>();

async function fetchSample(url: string): Promise<ArrayBuffer> {
  if (!rawSampleCache.has(url)) {
    const response = await fetch(url);
    rawSampleCache.set(url, await response.arrayBuffer());
  }
  return rawSampleCache.get(url)!.slice(0); // slice: give decoder its own copy
}

class SynthController extends TinyEmitter {

  public state = Vue.observable({
    isLoading: false
  });

  private _isLoaded: boolean = false;
  public get isLoaded() {
    return this._isLoaded;
  }
  private set isLoaded(value: boolean) {
    this._isLoaded = value;
  }

  private pianoSynth: Tone.Sampler | null = null;
  private melodySynth: Tone.Sampler | null = null;
  private metronomeSynth: Tone.Sampler | null = null;

  private currentArpeggioSequence: Tone.Sequence | null = null;

  /* Init */

  /**
   * If synth is loading or not loaded return true else return false
   */
  private initSynthIfNotLoaded() {
    if (this.isLoaded) {
      return false;
    }
    this.initSynth().then();
    return true;
  }

  public async initSynth(force = false) {
    const contextClosed = Tone.getContext().state === 'closed';
    if (contextClosed) {
      this.isLoaded = false;
    }
    if ((!this.isLoaded && !this.state.isLoading) || force) {
      this.state.isLoading = true;

      // Reset references — dispose first to detach audio nodes from the graph
      this.pianoSynth?.dispose();
      this.pianoSynth = null;
      this.melodySynth?.dispose();
      this.melodySynth = null;
      this.metronomeSynth?.dispose();
      this.metronomeSynth = null;

      try {
        // Load all samplers concurrently
        await Promise.all([
          this.initPianoSynth(),
          this.initMelodySynth(),
          this.initMetronome(),
        ]);

        // Mark as loaded once everything is done
        this.isLoaded = true;
        this.state.isLoading = false;

        if (!force) {
          this.emit('synthIsLoaded');
        }
      } catch (error) {
        console.error(error);
        Notify.create({
          type: 'negative',
          message: 'Une erreur est survenue lors du chargement des sons...',
          multiLine: true,
          actions: [
            {
              icon: 'mdi-refresh',
              label: 'Recharger la page', 
              color: 'white', 
              handler: () => {
                window.location.reload()
              }
            }
          ]
        });
      }
    }
  }

  private async initPianoSynth(): Promise<void> {
    // 1) fetch (or load from cache) and decode all audio
    const audioPromises = Object.entries(PIANO_SAMPLES).map(async ([key, url]) => {
      const rawBuffer = await fetchSample(url);
      const decodedBuffer = await Tone.getContext().decodeAudioData(rawBuffer);
      return { key, buffer: decodedBuffer };
    });
    const results = await Promise.all(audioPromises);

    // 2) create Sampler and populate it with preloaded buffers
    this.pianoSynth = new Tone.Sampler().toDestination();
    results.forEach(({ key, buffer }) => {
      this.pianoSynth!.add(key as any, buffer);
    });

    // 3) set your parameters
    this.pianoSynth.attack = 0;
    this.pianoSynth.release = 1;
    this.pianoSynth.volume.value = -10;
  }

  private async initMelodySynth(): Promise<void> {
    const audioPromises = Object.entries(FLUTE_SAMPLES).map(async ([key, url]) => {
      const rawBuffer = await fetchSample(url);
      const decodedBuffer = await Tone.getContext().decodeAudioData(rawBuffer);
      return { key, buffer: decodedBuffer };
    });
    const results = await Promise.all(audioPromises);

    this.melodySynth = new Tone.Sampler().toDestination();
    results.forEach(({ key, buffer }) => {
      this.melodySynth!.add(key as any, buffer);
    });

    this.melodySynth.attack = 0.5;
    this.melodySynth.release = 0.4;
    this.melodySynth.volume.value = -15;
  }

  private async initMetronome(): Promise<void> {
    const rawBuffer = await fetchSample(getAssetItem('audio/samples/metronome/metronome.mp3'));
    const decodedBuffer = await Tone.getContext().decodeAudioData(rawBuffer);

    this.metronomeSynth = new Tone.Sampler().toDestination();
    this.metronomeSynth.add('C1', decodedBuffer);

    this.metronomeSynth.volume.value = -10;
    this.metronomeSynth.release = 0;
  }

  /* Play (Player interfaces) */

  public playPiano(synthKeys: SynthKeys, volume: number = 1, time = Tone.immediate()) {
    this.playNotes(synthKeys, volume, this.pianoSynth!, time);
  }

  public playMelody(synthKeys: SynthKeys, volume: number = 1, time = Tone.immediate()) {
    this.playNotes(synthKeys, volume, this.melodySynth!, time);
  }

  // Fast paths used by AudioPlayer when note names are pre-computed at load time
  public playPianoPrecomputed(noteNames: string[], durations: string[], volume: number, time: number) {
    if (this.initSynthIfNotLoaded()) return;
    this.pianoSynth!.triggerAttackRelease(noteNames, durations, time, volume);
  }

  public playMelodyPrecomputed(noteNames: string[], durations: string[], volume: number, time: number) {
    if (this.initSynthIfNotLoaded()) return;
    this.melodySynth!.triggerAttackRelease(noteNames, durations, time, volume);
  }

  private playNotes(synthKeys: SynthKeys, volume: number, synth: Tone.Sampler, time = Tone.immediate()) {
    if (this.initSynthIfNotLoaded()) {
      return;
    }

    const notesToPlay: number[] = synthKeys.keys.map((note) => KEYS_TO_NOTES[note]);
    const durations: number[] | string[] = synthKeys.durations;
    synth.triggerAttackRelease(notesToPlay, durations, time, volume);
  }

  // TODO: [nplayer] will need to update it using Tone time
  public playArpeggio(synthKeys: SynthKeys) {
    if (this.initSynthIfNotLoaded()) {
      return;
    }

    if (this.currentArpeggioSequence != null) {
      this.stopArpeggio();
    }
    audioPlayer.stopPart();

    const notes = synthKeys.keys.map((key, index) => ({
      key,
      duration: synthKeys.durations[index] as number,
      index
    }));

    this.currentArpeggioSequence = new Tone.Sequence((time, note) => {
      this.playPiano(new SynthKeys([note.key], [note.duration]), 1, time);
      this.emit('arpeggioHighlightNote', note.key);
      if (note.index === (notes.length - 1)) {
        setTimeout(() => {
          this.stopArpeggio();
          this.emit('arpeggioEnded');
        }, 500);
      }
    }, notes, '4n');
    this.currentArpeggioSequence.loop = false;
    this.currentArpeggioSequence.start(0);

    if (Tone.getTransport().state !== 'started') {
      Tone.getTransport().start();
    }
  }

  public stopArpeggio() {
    this.currentArpeggioSequence?.stop();
    this.currentArpeggioSequence?.dispose();
    this.currentArpeggioSequence = null;
    if (Tone.getTransport().state === 'started') {
      Tone.getTransport().stop();
    }
  }

  public playMetronome(time = Tone.immediate(), volume = 100) {
    if (this.initSynthIfNotLoaded()) {
      return;
    }
    this.metronomeSynth!.releaseAll();
    this.metronomeSynth!.triggerAttackRelease('C1', '4n', time, volume / 100);
  }

  /* Trigger (Midi interactions) */

  public triggerAttack(synthKeyEvent: MidiKeyEvent) {
    if (this.initSynthIfNotLoaded()) {
      return;
    }
    if (KEYS_TO_NOTES[synthKeyEvent.key]) {
      this.pianoSynth!.triggerAttack(
        KEYS_TO_NOTES[synthKeyEvent.key],
        Tone.immediate(),
        synthKeyEvent.velocity / 127
      );
    }
  }

  public triggerRelease(synthKeyEvent: MidiKeyEvent) {
    if (this.initSynthIfNotLoaded()) {
      return;
    }
    if (KEYS_TO_NOTES[synthKeyEvent.key]) {
      this.pianoSynth!.triggerRelease(KEYS_TO_NOTES[synthKeyEvent.key]);
    }
  }

  public triggerReleaseAll() {
    if (this.initSynthIfNotLoaded()) {
      return;
    }
    this.pianoSynth!.releaseAll();
  }
}

export const synthController = new SynthController();
